import {createSlice} from '@reduxjs/toolkit';
import type {
  TargetPlatform,
  TargetPlatformWithDSPs,
} from 'common/src/modules/targetPlatform/targetPlatform.types';
import {VideoSettings} from '../../videoSettings/videoSettings.types';
import {initializeTargetPlatforms} from './thunks/initializeTargetPlatforms';
import {initializeSlimTargetPlatforms} from './thunks/initializeSlimTargetPlatforms';

/**
 * Interface defining the shape of the target platforms state in Redux store
 * @property platforms - Array of target platforms
 * @property videoSettings - Array of video settings associated with platforms
 * @property isLoading - Loading state indicator
 * @property error - Error message if any operation fails
 */
export interface TargetPlatformsState {
  platformsWithDSPs: TargetPlatformWithDSPs[];
  platforms: TargetPlatform[];
  videoSettings: VideoSettings[];
  isLoading: boolean;
  error: string | null;
}

/**
 * Initial state for the target platforms slice
 */
const initialState: TargetPlatformsState = {
  platformsWithDSPs: [],
  platforms: [],
  videoSettings: [],
  isLoading: false,
  error: null,
};

/**
 * Redux slice for managing target platforms and video settings
 * Handles CRUD operations for both platforms and video settings
 */
const targetPlatformsSlice = createSlice({
  name: 'targetPlatforms',
  initialState,
  reducers: {
    /**
     * Add a new target platform to the state
     */
    addTargetPlatformAction: (state, action) => {
      state.platformsWithDSPs.push(action.payload);
    },
    /**
     * Update an existing target platform in the state
     */
    updateTargetPlatformAction: (state, action) => {
      const updated = action.payload;
      const idx = state.platformsWithDSPs.findIndex((p) => p.id === updated.id);

      if (idx !== -1) {
        state.platformsWithDSPs[idx] = {...state.platformsWithDSPs[idx], ...updated};
      }
    },
    /**
     * Delete a target platform from the state
     */
    deleteTargetPlatformAction: (state, action) => {
      const id = action.payload;
      state.platformsWithDSPs = state.platformsWithDSPs.filter((p) => p.id !== id);
    },
    /**
     * Reset the platforms array with new data
     */
    resetTargetPlatformsAction: (state, action) => {
      state.platformsWithDSPs = action.payload;
    },
    /**
     * Add a new video setting to the state
     */
    addVideoSettingAction: (state, action) => {
      state.videoSettings.push(action.payload);
    },
    /**
     * Update an existing video setting in the state
     */
    updateVideoSettingAction: (state, action) => {
      const updated = action.payload;
      const idx = state.videoSettings.findIndex((v) => v.id === updated.id);

      if (idx !== -1) {
        state.videoSettings[idx] = {...state.videoSettings[idx], ...updated};
      }
    },
    /**
     * Delete a video setting from the state
     */
    deleteVideoSettingAction: (state, action) => {
      const id = action.payload;
      state.videoSettings = state.videoSettings.filter((v) => v.id !== id);
    },
    /**
     * Reset the video settings array with new data
     */
    resetVideoSettingsAction: (state, action) => {
      state.videoSettings = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle initialization pending state
      .addCase(initializeTargetPlatforms.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      // Handle successful initialization
      .addCase(initializeTargetPlatforms.fulfilled, (state, action) => {
        state.platformsWithDSPs = action.payload.platforms;
        state.videoSettings = action.payload.videoSettings;
        state.isLoading = false;
        state.error = null;
      })
      // Handle initialization failure
      .addCase(initializeTargetPlatforms.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) || 'Failed to initialize target platforms';
      })
      // Handle initialization pending state
      .addCase(initializeSlimTargetPlatforms.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      // Handle successful initialization
      .addCase(initializeSlimTargetPlatforms.fulfilled, (state, action) => {
        state.platforms = action.payload.platforms;
        state.videoSettings = action.payload.videoSettings;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(initializeSlimTargetPlatforms.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) || 'Failed to initialize target platforms';
      });
  },
});

// Export individual actions
const {
  addTargetPlatformAction,
  updateTargetPlatformAction,
  deleteTargetPlatformAction,
  resetTargetPlatformsAction,
  addVideoSettingAction,
  updateVideoSettingAction,
  deleteVideoSettingAction,
  resetVideoSettingsAction,
} = targetPlatformsSlice.actions;

export {
  addTargetPlatformAction,
  updateTargetPlatformAction,
  deleteTargetPlatformAction,
  resetTargetPlatformsAction,
  addVideoSettingAction,
  updateVideoSettingAction,
  deleteVideoSettingAction,
  resetVideoSettingsAction,
};

export default targetPlatformsSlice.reducer;
